import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NOTIFICATION_CHANNELS, type NotificationChannel } from '@qms360/shared';
import { ApiError } from '../../lib/api.js';
import { fetchTemplates, revertTemplate, saveTemplate, type TemplateView } from '../../lib/notifications-api.js';
import { t } from '../../lib/i18n.js';

/**
 * What each message says (spec §13.6, Rule 1).
 *
 * Which events send a message is fixed; the wording is the organisation's. Each
 * message lists the placeholders it can fill, and the server refuses any other —
 * a placeholder nothing fills would reach every reader as a blank.
 */

const CHANNEL_LABELS: Record<NotificationChannel, string> = {
  in_app: 'In the app',
  email: 'Email',
  push: 'Push',
  sms: 'SMS',
};

export function NotificationTemplatesSection() {
  const templates = useQuery({ queryKey: ['notification-templates'], queryFn: fetchTemplates, retry: false });
  // Reaching Master Data and reaching the wording share one rule; if this is
  // refused the section is simply not offered.
  if (!templates.data) return null;

  return (
    <section className="mt-8 rounded-md border border-border bg-surface p-5" aria-labelledby="templates-heading">
      <h2 id="templates-heading" className="m-0 text-lg font-semibold">
        {t("Notification wording")}
      </h2>
      <p className="mt-1 text-sm text-content-secondary">
        {t("What reminders, overdue notices and escalations say. Changing the wording takes effect from the next message.")}
      </p>
      <ul className="m-0 mt-4 list-none space-y-3 p-0">
        {templates.data.templates.map((template) => (
          <TemplateRow key={template.key} template={template} />
        ))}
      </ul>
    </section>
  );
}

function TemplateRow({ template }: { template: TemplateView }) {
  const queryClient = useQueryClient();
  const [channel, setChannel] = useState<NotificationChannel>('in_app');
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const override = template.overrides.find((row) => row.channel === channel) ?? null;
  const [draft, setDraft] = useState({ subjectEn: '', bodyEn: '', subjectAr: '', bodyAr: '' });

  const done = (data: { templates: TemplateView[] }) => {
    setError(null);
    setEditing(false);
    queryClient.setQueryData(['notification-templates'], data);
  };
  const fail = (caught: unknown) =>
    setError(caught instanceof ApiError ? caught.message : t("The wording could not be saved."));

  const save = useMutation({
    mutationFn: () =>
      saveTemplate(template.key, {
        channel,
        subjectEn: draft.subjectEn,
        bodyEn: draft.bodyEn,
        subjectAr: draft.subjectAr || null,
        bodyAr: draft.bodyAr || null,
      }),
    onSuccess: done,
    onError: fail,
  });
  const revert = useMutation({ mutationFn: () => revertTemplate(template.key, channel), onSuccess: done, onError: fail });

  const startEditing = () => {
    setDraft({
      subjectEn: override?.subjectEn ?? template.subjectEn,
      bodyEn: override?.bodyEn ?? template.bodyEn,
      subjectAr: override?.subjectAr ?? '',
      bodyAr: override?.bodyAr ?? '',
    });
    setEditing(true);
  };

  return (
    <li className="rounded border border-border p-3 text-sm" data-testid={`template-${template.key}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <strong>{t(template.label)}</strong>
        <span className="text-xs text-content-secondary">
          {override ? t("Customised") : t("Default wording")}
          {template.critical ? t(" · always delivered") : ''}
        </span>
      </div>
      <p className="mb-0 mt-1 text-xs text-content-secondary">{template.description}</p>

      <label className="mt-2 flex items-center gap-2 text-xs">
        <span className="text-content-secondary">{t("Channel")}</span>
        <select
          value={channel}
          onChange={(event) => {
            setChannel(event.target.value as NotificationChannel);
            setEditing(false);
          }}
          className="rounded border border-border px-2 py-0.5"
        >
          {NOTIFICATION_CHANNELS.map((option) => (
            <option key={option} value={option}>
              {t(CHANNEL_LABELS[option])}
            </option>
          ))}
        </select>
      </label>

      {!editing ? (
        <div className="mt-2 rounded bg-tone-info-surface p-2">
          <p className="m-0 font-medium">{override?.subjectEn ?? template.subjectEn}</p>
          <p className="mb-0 mt-1">{override?.bodyEn ?? template.bodyEn}</p>
        </div>
      ) : (
        <div className="mt-2 grid gap-2">
          {(['subjectEn', 'bodyEn', 'subjectAr', 'bodyAr'] as const).map((field) => (
            <label key={field} className="flex flex-col gap-1">
              <span className="text-xs text-content-secondary">
                {field.startsWith('subject') ? t("Subject") : t("Message")} ({field.endsWith('Ar') ? t("Arabic, optional") : t("English")})
              </span>
              {field.startsWith('subject') ? (
                <input
                  type="text"
                  dir={field.endsWith('Ar') ? 'rtl' : undefined}
                  value={draft[field]}
                  onChange={(event) => setDraft({ ...draft, [field]: event.target.value })}
                  className="rounded border border-border px-2 py-1"
                />
              ) : (
                <textarea
                  rows={2}
                  dir={field.endsWith('Ar') ? 'rtl' : undefined}
                  value={draft[field]}
                  onChange={(event) => setDraft({ ...draft, [field]: event.target.value })}
                  className="rounded border border-border px-2 py-1"
                />
              )}
            </label>
          ))}
          <p className="m-0 text-xs text-content-secondary">
            {t("Can use:")}{' '}{template.mergeFields.map((name) => `{{${name}}}`).join(', ')}
          </p>
        </div>
      )}

      {error && (
        <p role="alert" className="mb-0 mt-2 text-sm" style={{ color: 'var(--tone-rejected)' }}>
          {error}
        </p>
      )}

      {template.canEdit ? (
        <div className="mt-2 flex gap-2">
          {editing ? (
            <>
              <button
                type="button"
                disabled={save.isPending}
                onClick={() => save.mutate()}
                className="rounded bg-ag-purple px-3 py-1 text-white disabled:opacity-60"
              >
                {t("Save wording")}
              </button>
              <button type="button" onClick={() => setEditing(false)} className="rounded border border-border px-3 py-1">
                {t("Cancel")}
              </button>
            </>
          ) : (
            <button type="button" onClick={startEditing} className="rounded border border-border px-3 py-1">
              {t("Edit")}
            </button>
          )}
          {override && !editing && (
            <button
              type="button"
              disabled={revert.isPending}
              onClick={() => revert.mutate()}
              className="rounded border border-border px-3 py-1 disabled:opacity-60"
            >
              {t("Back to default")}
            </button>
          )}
        </div>
      ) : (
        <p className="mb-0 mt-2 text-xs text-content-secondary">
          {template.app
            ? t("Changing this needs configuration authority in the application that sends it.")
            : t("All three applications send this, so changing it needs configuration authority in all three.")}
        </p>
      )}
    </li>
  );
}
